// src/hooks/usePricingCalculation.js
import { useState, useEffect, useMemo, useCallback } from 'react';
import { detectItemKind, getPriceFromCatalogItem, toMl, toGrams, CONVERSIONS } from '../util/measurement';

/**
 * Custom hook for cocktail pricing calculations
 * Handles unit conversions, cost calculations, and invoice generation
 */
export default function usePricingCalculation({ 
  cocktails, 
  quantities = {},
  catalogData = {},
  onCostsUpdate,
  onInvoiceGenerate
}) {
  const [totalCosts, setTotalCosts] = useState({});
  const [aggregatedIngredients, setAggregatedIngredients] = useState({});
  const [invoiceData, setInvoiceData] = useState(null);

  // Keep STANDARD_BOTTLE_ML constant for defaults
  const STANDARD_BOTTLE_ML = 750;

  // Create stable references for dependencies
  const cocktailsStr = JSON.stringify(cocktails);
  const quantitiesStr = JSON.stringify(quantities);
  const catalogDataStr = JSON.stringify(catalogData);

  // Calculate costs for all cocktails when quantities or cocktails change
  useEffect(() => {
    const newCosts = {};
    const newAggregated = {};

    Object.entries(quantities).forEach(([cocktailId, quantity]) => {
      const cocktail = cocktails.find(c => c.id === cocktailId);
      if (cocktail && quantity > 0) {
        const costs = calculateCocktailCosts(cocktail, quantity);
        newCosts[cocktailId] = costs;
        
        // Aggregate ingredients for invoice
        aggregateIngredientsForCocktail(cocktail, quantity, newAggregated);
      }
    });

    setTotalCosts(newCosts);
    setAggregatedIngredients(newAggregated);
    // Callback removed to prevent infinite re-renders
    // onCostsUpdate?.(newCosts);
  }, [cocktailsStr, quantitiesStr, catalogDataStr]);

  // Generate invoice data when aggregated ingredients change
  useEffect(() => {
    const invoice = generateInvoiceData(aggregatedIngredients);
    setInvoiceData(invoice);
    // Callback removed to prevent infinite re-renders
    // onInvoiceGenerate?.(invoice);
  }, [JSON.stringify(aggregatedIngredients)]);

  const calculateCocktailCosts = (cocktail, quantity) => {
    const serviceFee = parseFloat(cocktail.serviceFee || cocktail.serviceFeePerCocktail || 0);
    let ingredientsCost = 0;
    let ingredientDetails = [];

    if (cocktail.ingredients) {
      cocktail.ingredients.forEach(ingredient => {
        const cost = calculateIngredientCost(ingredient, quantity);
        ingredientsCost += cost;
        ingredientDetails.push({
          name: ingredient.customIngredient?.name || ingredient.name,
          quantity: ingredient.qty || ingredient.amountPerCocktailOz || 0,
          unit: ingredient.unit || 'oz',
          costPerCocktail: cost / quantity,
          totalCost: cost,
          isCustom: ingredient.isCustom || !!ingredient.customIngredient,
          bottlesNeeded: getBottlesNeededForIngredient(ingredient, quantity),
          bottlePrice: getBottlePriceForIngredient(ingredient),
          catalogId: ingredient.catalogId
        });
      });
    }

    const perCocktailCost = (ingredientsCost + serviceFee * quantity) / quantity;
    const totalCost = ingredientsCost + serviceFee * quantity;

    return {
      quantity,
      perCocktailCost,
      totalCost,
      breakdown: {
        ingredients: ingredientsCost,
        service: serviceFee * quantity,
        total: totalCost
      },
      ingredientDetails
    };
  };

  const calculateIngredientCost = (ingredient, quantity = 1) => {
  const qty = parseFloat(ingredient.qty || ingredient.amountPerCocktailOz || ingredient.amountPerCocktailLeaves || 0);
    const ingredientName = ingredient.customIngredient?.name || ingredient.name || 'Unknown';
    
    // console.log(`🧪 Processing ingredient: ${ingredientName}`);
    // console.log(`   Quantity per cocktail: ${qty} ${ingredient.unit || 'oz'}`);
    // console.log(`   Total cocktails: ${quantity}`);
    // console.log(`   Is custom: ${ingredient.isCustom || !!ingredient.customIngredient}`);
    // console.log(`   Catalog ID: ${ingredient.catalogId}`);
    
    // Handle custom ingredients
    if (ingredient.customIngredient || ingredient.isCustom) {
      // console.log(`   ✅ Processing as custom ingredient`);
      const customData = ingredient.customIngredient || ingredient;
      if (customData.isWet === false && customData.isWeight !== true) {
        // Units by count
        const pricePerUnit = parseFloat(customData.pricePerUnit || 0);
        const totalUnitsNeeded = Math.ceil(qty * quantity);
        return totalUnitsNeeded * pricePerUnit;
      }
      if (customData.isWeight === true) {
        // Weight-based custom item: assume custom qty unit is grams unless specified
        const pricePerUnit = parseFloat(customData.pricePerUnit || 0);
        const gramsPerPack = parseFloat(customData.gramsPerPack || 0) || 1;
        const perCocktailGrams = toGrams(qty, ingredient.unit || 'g');
        const totalGrams = perCocktailGrams * quantity;
        const packsNeeded = Math.ceil(totalGrams / gramsPerPack);
        return packsNeeded * pricePerUnit;
      }
      // Default custom wet (liquid) by bottle-size
      const pricePerUnit = parseFloat(customData.pricePerUnit || 0);
      const qtyInMl = toMl(qty, ingredient.unit || 'oz');
      const bottleSizeMl = customData.bottleSizeMl || STANDARD_BOTTLE_ML;
      const totalAmountNeededMl = qtyInMl * quantity;
      const bottlesNeeded = Math.ceil(totalAmountNeededMl / bottleSizeMl);
      return bottlesNeeded * pricePerUnit;
    }
    
    // Handle catalog ingredients - use actual bottle prices, whole bottles only
    if (ingredient.catalogId && catalogData[ingredient.catalogId]) {
      // console.log(`   ✅ Processing as catalog ingredient`);
      const catalogItem = catalogData[ingredient.catalogId];
      const price = getPriceFromCatalogItem(catalogItem);
      const kind = detectItemKind(catalogItem);
      // Provider-controlled input: do not force defaults; use ingredient.unit
      if (kind.kind === 'liquid') {
        const perCocktailMl = ingredient.amountPerCocktailOz
          ? ingredient.amountPerCocktailOz * CONVERSIONS.OZ_TO_ML
          : toMl(qty, ingredient.unit || 'oz');
        const totalMl = perCocktailMl * quantity;
        const capacityMl = parseFloat(kind.capacityMl || STANDARD_BOTTLE_ML) || STANDARD_BOTTLE_ML;
        const packs = Math.ceil(totalMl / capacityMl);
        return packs * price;
      }
      if (kind.kind === 'unit') {
        const perCocktailUnits = qty; // assume qty already in units
        const totalUnits = perCocktailUnits * quantity;
        const capacityUnits = parseFloat(kind.capacityUnits || 1) || 1;
        const packs = Math.ceil(totalUnits / capacityUnits);
        return packs * price;
      }
      if (kind.kind === 'weight') {
        // Convert provider input into grams (if oz/lb/kg used)
        const perCocktailGrams = toGrams(qty, ingredient.unit || 'g');
        const totalGrams = perCocktailGrams * quantity;
        const capacityGrams = parseFloat(kind.capacityGrams || 1) || 1;
        const packs = Math.ceil(totalGrams / capacityGrams);
        return packs * price;
      }
      // Unknown -> return 0 to avoid mispricing
      return 0;
    }
    
    // console.log(`   ❌ No pricing method found - returning $0`);
    // console.log(`   catalogData keys:`, Object.keys(catalogData).slice(0, 10));
    return 0;
  };

  const convertToMl = (quantity, unit) => toMl(quantity, unit);

  const getBottlesNeededForIngredient = (ingredient, quantity = 1) => {
    const qty = parseFloat(ingredient.qty || ingredient.amountPerCocktailOz || 0);
    
    if (ingredient.customIngredient?.isWet === false || ingredient.isWet === false) {
      return 0;
    }
    
    if (ingredient.catalogId && catalogData[ingredient.catalogId]) {
      const catalogItem = catalogData[ingredient.catalogId];
      const kind = detectItemKind(catalogItem);
      if (kind.kind === 'liquid') {
        const amountNeededMl = ingredient.amountPerCocktailOz 
          ? ingredient.amountPerCocktailOz * CONVERSIONS.OZ_TO_ML
          : convertToMl(qty, ingredient.unit || 'oz');
        const totalAmountNeededMl = amountNeededMl * quantity;
        const capacityMl = parseFloat(kind.capacityMl || STANDARD_BOTTLE_ML) || STANDARD_BOTTLE_ML;
        return Math.ceil(totalAmountNeededMl / capacityMl);
      }
      if (kind.kind === 'unit') {
        const totalUnits = qty * quantity;
        const capacityUnits = parseFloat(kind.capacityUnits || 1) || 1;
        return Math.ceil(totalUnits / capacityUnits);
      }
      if (kind.kind === 'weight') {
        const totalGrams = toGrams(qty, ingredient.unit || 'g') * quantity;
        const capacityGrams = parseFloat(kind.capacityGrams || 1) || 1;
        return Math.ceil(totalGrams / capacityGrams);
      }
      return 0;
    }
    
    const amountNeededMl = convertToMl(qty, ingredient.unit || 'oz');
    const totalAmountNeededMl = amountNeededMl * STANDARD_BOTTLE_ML; // legacy fallback
    return Math.ceil(totalAmountNeededMl / STANDARD_BOTTLE_ML);
  };

  const getBottlePriceForIngredient = (ingredient) => {
    if (ingredient.catalogId && catalogData[ingredient.catalogId]) {
  return getPriceFromCatalogItem(catalogData[ingredient.catalogId]);
    }
    
    if (ingredient.customIngredient || ingredient.isCustom) {
      const customData = ingredient.customIngredient || ingredient;
      if (customData.isWet !== false) {
        const pricePerUnit = parseFloat(customData.pricePerUnit || 0);
        return pricePerUnit;
      }
    }
    
    return 0;
  };

  const aggregateIngredientsForCocktail = (cocktail, quantity, aggregated) => {
    if (!cocktail.ingredients) return;

    cocktail.ingredients.forEach(ingredient => {
      const ingredientName = ingredient.customIngredient?.name || ingredient.name || 'Unknown';
      const qty = parseFloat(ingredient.qty || ingredient.amountPerCocktailOz || 0);
      const unit = ingredient.unit || 'oz';
      const totalQuantityNeeded = qty * quantity;

      if (!aggregated[ingredientName]) {
        aggregated[ingredientName] = {
          name: ingredientName,
          totalQuantity: 0,
          unit: unit,
          bottlesNeeded: 0,
          bottlePrice: getBottlePriceForIngredient(ingredient),
          totalBottleCost: 0,
          isCustom: ingredient.isCustom || !!ingredient.customIngredient,
          catalogId: ingredient.catalogId,
          usedInCocktails: []
        };
      }

      const quantityInMl = convertToMl(totalQuantityNeeded, unit);
      aggregated[ingredientName].totalQuantity += quantityInMl;
      aggregated[ingredientName].usedInCocktails.push({
        cocktailName: cocktail.name,
        quantity: quantity,
        amountPerCocktail: qty,
        unit: unit
      });

      if (ingredient.customIngredient?.isWet !== false && !ingredient.isCustom) {
        if (ingredient.catalogId && catalogData[ingredient.catalogId]) {
          const catalogItem = catalogData[ingredient.catalogId];
          const volumeMl = parseFloat(catalogItem.volume_ml || CONVERSIONS.STANDARD_BOTTLE_ML);
          aggregated[ingredientName].bottlesNeeded = Math.ceil(aggregated[ingredientName].totalQuantity / volumeMl);
          aggregated[ingredientName].totalBottleCost = aggregated[ingredientName].bottlesNeeded * aggregated[ingredientName].bottlePrice;
        } else {
          aggregated[ingredientName].bottlesNeeded = Math.ceil(aggregated[ingredientName].totalQuantity / CONVERSIONS.STANDARD_BOTTLE_ML);
          aggregated[ingredientName].totalBottleCost = aggregated[ingredientName].bottlesNeeded * aggregated[ingredientName].bottlePrice;
        }
      } else {
        aggregated[ingredientName].bottlesNeeded = Math.ceil(aggregated[ingredientName].totalQuantity);
        aggregated[ingredientName].totalBottleCost = aggregated[ingredientName].bottlesNeeded * aggregated[ingredientName].bottlePrice;
      }
    });
  };

  const generateInvoiceData = (aggregated) => {
    const ingredientsList = Object.values(aggregated);
    const totalBottles = ingredientsList.reduce((sum, ing) => sum + ing.bottlesNeeded, 0);
    
    return {
      timestamp: new Date().toISOString(),
      totalIngredients: ingredientsList.length,
      totalBottlesNeeded: totalBottles,
      ingredients: ingredientsList,
      cocktailBreakdown: Object.entries(quantities).map(([cocktailId, quantity]) => {
        const cocktail = cocktails.find(c => c.id === cocktailId);
        const costs = totalCosts[cocktailId];
        return {
          cocktailId,
          cocktailName: cocktail?.name || 'Unknown',
          quantity,
          costs: costs || null
        };
      }).filter(item => item.quantity > 0)
    };
  };

  // Memoized total calculations
  const grandTotals = useMemo(() => {
    const totals = Object.values(totalCosts);
    return {
      totalCocktails: totals.reduce((sum, cost) => sum + cost.quantity, 0),
      totalCost: totals.reduce((sum, cost) => sum + cost.totalCost, 0),
      totalIngredientsCost: totals.reduce((sum, cost) => sum + cost.breakdown.ingredients, 0),
      totalServiceFees: totals.reduce((sum, cost) => sum + cost.breakdown.service, 0)
    };
  }, [totalCosts]);

  return {
    // Calculation results
    totalCosts,
    aggregatedIngredients,
    invoiceData,
    grandTotals,
    
    // Utility functions
    calculateCocktailCosts,
    calculateIngredientCost,
    convertToMl,
    
    // Constants
    CONVERSIONS
  };
}
